import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { API } from "../lib/api";
import { KeyRound, LogOut, Inbox, CalendarCheck, RefreshCw, Loader2 } from "lucide-react";
import SectionHeader from "../components/SectionHeader";

const TOKEN_KEY = "logdrive_admin_token";
const HTTP_UNAUTHORIZED = 401;
const INQUIRIES_COLS = ["company", "contact", "area", "flags", "notes", "received"];
const MEETINGS_COLS = ["name", "company", "contact", "when", "topic", "received"];

async function adminFetch(token, path) {
  const res = await fetch(`${API}${path}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (res.status === HTTP_UNAUTHORIZED) {
    const err = new Error("Unauthorized");
    err.status = HTTP_UNAUTHORIZED;
    throw err;
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export default function AdminPage() {
  const [token, setToken] = useState("");
  const [tokenInput, setTokenInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = sessionStorage.getItem(TOKEN_KEY) || "";
    if (t) {
      setToken(t);
    }
  }, []);

  const refresh = useCallback(
    async (override) => {
      const t = override ?? token;
      if (!t) return;
      setBusy(true);
      setError("");
      try {
        const [inq, mts] = await Promise.all([
          adminFetch(t, "/partnership-inquiry"),
          adminFetch(t, "/meeting-request"),
        ]);
        setInquiries(inq);
        setMeetings(mts);
      } catch (e) {
        if (e.status === HTTP_UNAUTHORIZED) {
          setError("Invalid or expired admin token.");
          sessionStorage.removeItem(TOKEN_KEY);
          setToken("");
          setInquiries([]);
          setMeetings([]);
        } else {
          setError(e.message || "Failed to load data.");
        }
      } finally {
        setBusy(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (token) void refresh(token);
  }, [token, refresh]);

  async function onLogin(e) {
    e.preventDefault();
    const t = tokenInput.trim();
    if (!t) return;
    setBusy(true);
    setError("");
    try {
      // Verify by fetching one endpoint
      await adminFetch(t, "/partnership-inquiry");
      sessionStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setTokenInput("");
      toast.success("Admin session active.");
    } catch (e) {
      if (e.status === HTTP_UNAUTHORIZED) {
        setError("Invalid admin token.");
      } else {
        setError(e.message);
      }
    } finally {
      setBusy(false);
    }
  }

  function logout() {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setInquiries([]);
    setMeetings([]);
  }

  if (!token) {
    return (
      <main data-testid="page-admin-login" className="min-h-[70vh] flex items-center justify-center px-6 py-20">
        <Helmet>
          <title>Admin · Logarithmic Technologies</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md border border-white/15 p-8 bg-[#0a0a0a]"
        >
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
            / admin · restricted
          </div>
          <h1 className="mt-3 font-display font-black text-3xl tracking-tighter">
            Admin access
          </h1>
          <p className="mt-3 text-white/60 text-sm leading-relaxed">
            Enter the shared admin token to view partnership inquiries and meeting
            requests. This area is for internal staff only.
          </p>

          <label htmlFor="admin-token" className="mt-8 block font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
            <KeyRound size={12} className="inline mr-2" /> Admin token
          </label>
          <input
            id="admin-token"
            data-testid="admin-token-input"
            type="password"
            autoComplete="off"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            className="mt-2 w-full bg-transparent border border-white/15 focus:border-white px-3 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none font-mono-tech"
            placeholder="paste admin token…"
          />

          {error && (
            <div data-testid="admin-login-error" className="mt-4 border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2 text-[#FF9E99] font-mono-tech text-[11px] uppercase tracking-widest">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-[#FF3B30] hover:bg-[#FF5C53] disabled:opacity-50 text-white font-mono-tech uppercase tracking-widest text-xs px-6 py-4"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : null}
            {busy ? "Verifying…" : "Authenticate"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="page-admin" className="min-h-[70vh]">
      <Helmet>
        <title>Admin · Logarithmic Technologies</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <section className="pt-20 pb-8 border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <SectionHeader kicker="/ admin console" title={<>Partnership<br />inbox.</>} />
          <div className="flex items-center gap-3">
            <button
              onClick={() => refresh()}
              disabled={busy}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-white px-4 py-3 font-mono-tech uppercase tracking-widest text-[11px]"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-white px-4 py-3 font-mono-tech uppercase tracking-widest text-[11px]"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="py-8 border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center gap-2">
          <TabBtn active={tab === "inquiries"} onClick={() => setTab("inquiries")} testid="tab-inquiries">
            <Inbox size={12} /> Inquiries · {inquiries.length}
          </TabBtn>
          <TabBtn active={tab === "meetings"} onClick={() => setTab("meetings")} testid="tab-meetings">
            <CalendarCheck size={12} /> Meetings · {meetings.length}
          </TabBtn>
        </div>
      </section>

      <section className="py-10 pb-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          {error && (
            <div className="mb-6 border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-4 py-3 text-[#FF9E99] font-mono-tech text-[11px] uppercase tracking-widest">
              {error}
            </div>
          )}

          {tab === "inquiries" && (
            <div data-testid="admin-inquiries-list" className="border border-white/10">
              <HeaderRow cols={INQUIRIES_COLS} />
              {inquiries.length === 0 && <Empty label="No partnership inquiries yet." />}
              {inquiries.map((x) => (
                <div
                  key={x.id}
                  className="grid grid-cols-12 gap-2 px-4 py-4 border-t border-white/10 text-sm"
                >
                  <div className="col-span-2 font-display font-bold">{x.company}</div>
                  <div className="col-span-3">
                    <div className="text-white/90">{x.name}</div>
                    <div className="font-mono-tech text-[11px] text-white/50 break-all">{x.email}</div>
                    {x.role && <div className="font-mono-tech text-[10px] text-white/40 uppercase tracking-widest">{x.role}</div>}
                  </div>
                  <div className="col-span-1 font-mono-tech text-[10px] uppercase tracking-widest text-[#FF3B30]">
                    {x.interest_area || "—"}
                  </div>
                  <div className="col-span-1 flex flex-col gap-1 font-mono-tech text-[10px] uppercase tracking-widest text-white/70">
                    {x.briefing_requested ? <span>briefing</span> : null}
                    {x.capabilities_deck_requested ? <span>deck</span> : null}
                  </div>
                  <div className="col-span-3 text-white/70 text-sm">{x.message || "—"}</div>
                  <div className="col-span-2 font-mono-tech text-[10px] uppercase tracking-widest text-white/40">
                    {formatDate(x.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "meetings" && (
            <div data-testid="admin-meetings-list" className="border border-white/10">
              <HeaderRow cols={MEETINGS_COLS} />
              {meetings.length === 0 && <Empty label="No meeting requests yet." />}
              {meetings.map((m) => (
                <div
                  key={m.id}
                  className="grid grid-cols-12 gap-2 px-4 py-4 border-t border-white/10 text-sm"
                >
                  <div className="col-span-2 font-display font-bold">{m.name}</div>
                  <div className="col-span-2 text-white/80">{m.company}</div>
                  <div className="col-span-2 font-mono-tech text-[11px] text-white/50 break-all">{m.email}</div>
                  <div className="col-span-2 font-mono-tech text-[11px] text-white">
                    {m.preferred_date} · {m.preferred_time} {m.timezone_label}
                  </div>
                  <div className="col-span-2 text-white/70 text-sm">{m.topic || "—"}</div>
                  <div className="col-span-2 font-mono-tech text-[10px] uppercase tracking-widest text-white/40">
                    {formatDate(m.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 border px-4 py-3 font-mono-tech uppercase tracking-widest text-[11px] ${
        active
          ? "bg-white text-black border-white"
          : "border-white/15 text-white/70 hover:text-white hover:border-white/40"
      }`}
    >
      {children}
    </button>
  );
}

function HeaderRow({ cols }) {
  return (
    <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#0a0a0a] font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
      <div className="col-span-2">{cols[0]}</div>
      <div className={cols.length === 6 ? "col-span-3" : "col-span-2"}>{cols[1]}</div>
      <div className={cols.length === 6 ? "col-span-1" : "col-span-2"}>{cols[2]}</div>
      <div className={cols.length === 6 ? "col-span-1" : "col-span-2"}>{cols[3]}</div>
      <div className={cols.length === 6 ? "col-span-3" : "col-span-2"}>{cols[4]}</div>
      <div className="col-span-2">{cols[5]}</div>
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="px-4 py-12 text-center font-mono-tech text-[11px] uppercase tracking-widest text-white/40">
      {label}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toISOString().replace("T", " ").slice(0, 16);
  } catch {
    return iso;
  }
}
